import { useCallback, useEffect, useRef, useState } from 'react';
import { isDeepEqual } from '../../utils/isDeepEqual';

const serialize = (value: unknown): { text: string; error: string } => {
    try {
        return { text: JSON.stringify(value ?? {}, null, 2), error: '' };
    } catch {
        return { text: '{}', error: 'Unable to serialize current form data.' };
    }
};

export const useRawJsonDraft = <T,>(value: T | undefined, onApply: (parsed: T) => void, enabled: boolean) => {
    const [text, setText] = useState<string>('');
    const [error, setError] = useState<string>('');
    const syncedRef = useRef(false);
    const lastValueRef = useRef<T | undefined>(value);

    useEffect(() => {
        if (!enabled || (syncedRef.current && isDeepEqual(lastValueRef.current, value))) {
            return;
        }
        syncedRef.current = true;
        lastValueRef.current = value;
        const next = serialize(value);
        setText(next.text);
        setError(next.error);
    }, [value, enabled]);

    const commit = useCallback((override?: string, { silent = false }: { silent?: boolean } = {}): boolean => {
        const source = typeof override === 'string' ? override : text;
        let parsed: T;
        try {
            parsed = (source.trim() ? JSON.parse(source) : {}) as T;
        } catch (err: any) {
            if (!silent) {
                setError(`Invalid JSON: ${err?.message || 'fix it before switching back to the form.'}`);
            }
            return false;
        }
        lastValueRef.current = parsed;
        onApply(parsed);
        setError('');
        return true;
    }, [text, onApply]);

    return { text, setText, error, commit };
};
